/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 01/12/2025 - 17:02:38
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 01/12/2025
    * - Author          : USER
    * - Modification    : 
**/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { motion } from 'framer-motion';
import { FaUser, FaUsers, FaSchool, FaCalendar, FaPhone, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../services/api';


const CompleteProfile = () => {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const [loading, setLoading] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    registrationType: '',
    phone: '',
    age: '',
    school: '',
    classGrade: '',
    teamName: '',
    teamSize: '',
    coachName: '',
    coachEmail: '',
    coachPhone: '',
    schoolName: '',
    numberOfStudents: '',
    location: '',
    principalName: '',
    principalEmail: '',
    principalPhone: ''
  });

  // Check if user is authenticated and if profile is already completed
  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (!isLoaded) return;

      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        const response = await api.get('/auth/profile');
        
        if (response?.user?.registrationType) {
          toast.success('Profile already completed!');
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (error) {
        console.log('Profile not found, showing completion form');
      } finally {
        setCheckingProfile(false);
      }
    };

    checkProfileCompletion();
  }, [isLoaded, user, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Call backend to complete profile
      // Note: api interceptor returns response.data directly, so response is already the data object
      const response = await api.post('/auth/complete-profile', {
        clerkId: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        // Use formData.phone first (from form input), then fall back to Clerk phone or other phones
        phone: formData.phone || user.primaryPhoneNumber?.phoneNumber || formData.coachPhone || formData.principalPhone,
        ...formData
      });

      if (response.success) {
        console.log('✅ Profile completed, response:', response);
        toast.success(response.message || 'Profile completed successfully!');
        // After completing profile, redirect to dashboard. We no longer store a
        // local flag in localStorage — profile completion is determined by the
        // presence of a backend profile queried via Clerk tokens.
  console.log('🚀 Navigating to dashboard...');
  navigate('/dashboard', { replace: true });
      } else {
        console.error('❌ Profile completion failed:', response);
        throw new Error(response.message || 'Profile completion failed');
      }
    } catch (error) {
      console.error('Complete profile error:', error);
      toast.error(error.message || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  const renderRegistrationTypeSelection = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
        Complete Your Profile
      </h2>
      <p className="text-gray-600 text-center mb-8">
        Please select how you want to register
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Individual */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setFormData({ ...formData, registrationType: 'individual' });
            setStep(2);
          }}
          className="p-6 border-2 border-gray-200 rounded-2xl cursor-pointer hover:border-green-500 transition-all"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <FaUser className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Individual</h3>
            <p className="text-sm text-gray-600">Register as an individual debater</p>
          </div>
        </motion.div>

        {/* Team */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setFormData({ ...formData, registrationType: 'team' });
            setStep(2);
          }}
          className="p-6 border-2 border-gray-200 rounded-2xl cursor-pointer hover:border-blue-500 transition-all"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <FaUsers className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Team</h3>
            <p className="text-sm text-gray-600">Register as a debate team</p>
          </div>
        </motion.div>

        {/* School */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setFormData({ ...formData, registrationType: 'school' });
            setStep(2);
          }}
          className="p-6 border-2 border-gray-200 rounded-2xl cursor-pointer hover:border-purple-500 transition-all"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <FaSchool className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">School</h3>
            <p className="text-sm text-gray-600">Register your entire school</p>
          </div>
        </motion.div>
      </div>
    </div>
  );

  const renderIndividualForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Individual Registration</h2>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          placeholder="+254700000000"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Age <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          name="age"
          value={formData.age}
          onChange={handleChange}
          required
          min="5"
          max="100"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          School <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="school"
          value={formData.school}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Class/Grade <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="classGrade"
          value={formData.classGrade}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div className="flex space-x-4 pt-4">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Completing...
            </>
          ) : (
            'Complete Profile'
          )}
        </button>
      </div>
    </form>
  );

  const renderTeamForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Team Registration</h2>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          placeholder="+254700000000"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Team Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="teamName"
          value={formData.teamName}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          School/Organization Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="school"
          value={formData.school}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Team Size <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          name="teamSize"
          value={formData.teamSize}
          onChange={handleChange}
          required
          min="1"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Coach Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="coachName"
          value={formData.coachName}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Coach Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="coachEmail"
          value={formData.coachEmail}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Coach Phone <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="coachPhone"
          value={formData.coachPhone}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex space-x-4 pt-4">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Completing...
            </>
          ) : (
            'Complete Profile'
          )}
        </button>
      </div>
    </form>
  );

  const renderSchoolForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">School Registration</h2>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          placeholder="+254700000000"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          School Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="schoolName"
          value={formData.schoolName}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          School Location <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="location"
          value={formData.location}
          onChange={handleChange}
          required
          placeholder="e.g., Nairobi, Kenya"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Number of Students <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          name="numberOfStudents"
          value={formData.numberOfStudents}
          onChange={handleChange}
          required
          min="1"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Principal Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="principalName"
          value={formData.principalName}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Principal Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="principalEmail"
          value={formData.principalEmail}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Principal Phone <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="principalPhone"
          value={formData.principalPhone}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div className="flex space-x-4 pt-4">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Completing...
            </>
          ) : (
            'Complete Profile'
          )}
        </button>
      </div>
    </form>
  );

  // Show loading while checking authentication and profile status
  if (!isLoaded || checkingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FaSpinner className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  // If not signed in, redirect is handled by useEffect
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-2xl"
      >
        {step === 1 && renderRegistrationTypeSelection()}
        {step === 2 && formData.registrationType === 'individual' && renderIndividualForm()}
        {step === 2 && formData.registrationType === 'team' && renderTeamForm()}
        {step === 2 && formData.registrationType === 'school' && renderSchoolForm()}
      </motion.div>
    </div>
  );
};

export default CompleteProfile;
